import { useEffect, useState } from "react";
import { MessageSquareQuote, Check, X, RotateCcw, Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-600",
};

export default function FounderTestimonials() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/founder/testimonials").then(({ data }) => setItems(data.testimonials)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const moderate = async (id, status) => {
    setBusy(id + status);
    try {
      await api.patch(`/founder/testimonials/${id}`, { status });
      toast.success(status === "approved" ? "Published to the landing page." : status === "rejected" ? "Rejected." : "Moved back to pending.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update."); }
    finally { setBusy(null); }
  };

  const pending = (items || []).filter((t) => t.status === "pending");
  const others = (items || []).filter((t) => t.status !== "pending");

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-8 py-10 ltifade" data-testid="founder-testimonials">
        <div className="flex items-center gap-2">
          <MessageSquareQuote size={20} className="text-brand" />
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Testimonials</h1>
        </div>
        <p className="mt-1 text-slate-500">Approve real builder stories to show them on the public landing page. Nothing appears publicly until you approve it.</p>

        {items === null ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-brand-line bg-white p-6 text-slate-500" data-testid="testimonials-empty">
            No submissions yet. Builders are invited to share after they launch or make their first sale.
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {pending.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Awaiting review ({pending.length})</p>
                <div className="mt-2 space-y-3">{pending.map((t) => <Card key={t.testimonial_id} t={t} busy={busy} moderate={moderate} />)}</div>
              </div>
            )}
            {others.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Reviewed</p>
                <div className="mt-2 space-y-3">{others.map((t) => <Card key={t.testimonial_id} t={t} busy={busy} moderate={moderate} />)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Card({ t, busy, moderate }) {
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4" data-testid={`testimonial-row-${t.testimonial_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {t.photo ? <img src={t.photo} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
            : <span className="grid h-11 w-11 place-items-center rounded-full bg-brand-surface text-sm font-semibold text-brand">{(t.name || "?").charAt(0).toUpperCase()}</span>}
          <div>
            <p className="text-sm font-semibold text-brand-deep">{t.name} <span className="font-normal text-slate-400">· {t.business_name || "—"}</span></p>
            <p className="mt-1 text-sm text-slate-600">"{t.quote}"</p>
            <p className="mt-1 text-[11px] text-slate-400">Triggered by {t.trigger === "first_sale" ? "first sale" : "launch"}</p>
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[t.status]}`}>{t.status}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {t.status !== "approved" && (
          <button data-testid={`approve-testimonial-${t.testimonial_id}`} onClick={() => moderate(t.testimonial_id, "approved")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
            {busy === t.testimonial_id + "approved" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve & publish
          </button>
        )}
        {t.status !== "rejected" && (
          <button data-testid={`reject-testimonial-${t.testimonial_id}`} onClick={() => moderate(t.testimonial_id, "rejected")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
            <X size={13} /> Reject
          </button>
        )}
        {t.status !== "pending" && (
          <button data-testid={`reset-testimonial-${t.testimonial_id}`} onClick={() => moderate(t.testimonial_id, "pending")} disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50">
            <RotateCcw size={13} /> Move to pending
          </button>
        )}
      </div>
    </div>
  );
}
